import React, { useState, useEffect } from "react";
import LoadingSpinner from "../LoadingSpinner";
import MessageModel from "../MessageModel";
import {
  addMedicalInformationFamily,
  drpACES,
  getPatientMedicalInfo,
  updateMedicalInformationFamily,
} from "../../functions/patient";
import VoiceToText from "../VoiceToText";
import EditButton from "../EditButton";
import DescriptionInput from "../DescriptionInput";


/* --------------------------------------------------------------
   Print-Only CSS: A4, page breaks, color accuracy
   -------------------------------------------------------------- */
const printStyles = `
  @media print {
    @page { size: A4; margin: 1cm; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-break { page-break-before: always; }
  }
  @media screen {
    .print-break { 
      break-before: page;
      margin-top: 30mm;
    }
  }
`;

/* --------------------------------------------------------------
   Print View: Family Mental Health (Husband + Wife)
   -------------------------------------------------------------- */
const PrintMentalHealthFamilyA4 = ({ medicalInformation }) => {

  // Reusable: render string, list, or N/A
  const renderValue = (value) => {
    if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === "string" && value.trim() === "")) {
      return <span className="text-gray-500 italic">N/A</span>;
    }

    if (Array.isArray(value)) {
      return (
        <ul className="list-disc list-inside mt-2 space-y-1">
          {value.map((item, i) => (
            <li key={i} className="text-gray-800">{item}</li>
          ))}
        </ul>
      );
    }

    if (typeof value === "string" && value.includes(";")) {
      const items = value.split(";").map(s => s.trim()).filter(Boolean);
      return (
        <ul className="list-disc list-inside mt-2 space-y-1">
          {items.map((item, i) => (
            <li key={i} className="text-gray-800">{item}</li>
          ))}
        </ul>
      );
    }

    return <div className="text-gray-800 whitespace-pre-line">{value}</div>;
  };

  const renderYesNo = (value) => {
    return value ? "Yes" : "No";
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: printStyles }} />

      <div className="print-break font-sans text-sm leading-relaxed max-w-[210mm] mx-auto bg-white">

        {/* ========== PAGE 1: Title + Health Details ========== */}
        <div>
          <h1 className="text-center text-xl font-bold text-sky-700 mb-6">
            Family Mental Health Details
          </h1>

          {/* Column Headers */}
          <div className="grid grid-cols-5 gap-4 mb-4 font-semibold text-sky-700 text-sm">
            <div></div>
            <div className="col-span-2 text-center">Husband</div>
            <div className="col-span-2 text-center">Wife</div>
          </div>

          {/* ---------- Health Details ---------- */}
          <h3 className="text-lg font-bold text-sky-700 border-b-2 border-sky-700 pb-1 mb-4">
            Health Details
          </h3>


          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              Present Complaints
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.presentComplaintsHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.presentComplaintsWife.value)}
              </div>
            </div>
          </div>


         <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
            History of Present Complaints
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfPresentComplaintsHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfPresentComplaintsWife.value)}
              </div>
            </div>
          </div>


    <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
            Past Medical History
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.passMedicalHistoryHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.passMedicalHistoryWife.value)}
              </div>
            </div>
          </div>



          {/* Main Complaints */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              History of Substance Abuse
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfSubstanceAbuseHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfSubstanceAbuseWife.value)}
              </div>
            </div>
          </div>

          {/* Worse Conditions */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              Psychiatric Family History
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.psychiatricFamilyHistoryHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.psychiatricFamilyHistoryWife.value)}
              </div>
            </div>
          </div>

  
        </div>

        {/* ========== PAGE 2: Treatment History ========== */}
        <div className="print-break">

          <h3 className="text-lg font-bold text-sky-700 border-b-2 border-sky-700 pb-1 mb-4">
            Treatment History
          </h3>

          {/* Psychiatric Treatment History */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              Past History of Psychiatric Treatment or Counselling
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4 text-center font-medium">
                {renderYesNo(medicalInformation.isHistoryOfPsychiatricTreatmentsHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4 text-center font-medium">
                {renderYesNo(medicalInformation.isHistoryOfPsychiatricTreatmentsWife.value)}
              </div>
            </div>
          </div>

          {/* Psychiatric Hospitalization */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              Psychiatric Hospitalization
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4 text-center font-medium">
                {renderYesNo(medicalInformation.isPsychiatricHospitalizationHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4 text-center font-medium">
                {renderYesNo(medicalInformation.isPsychiatricHospitalizationWife.value)}
              </div>
            </div>
          </div>

          {/* Mental Illness History */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              History of Mental Illness
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfMentalIllnessHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfMentalIllnessWife.value)}
              </div>
            </div>
          </div>

          {/* Substance Abuse History */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              History of Substance Abuse
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfSubstanceAbuseHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfSubstanceAbuseWife.value)}
              </div>
            </div>
          </div>

          {/* ACES History */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              History of ACES (Adverse Childhood Experiences)
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfACESHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.historyOfACESWife.value)}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-5 gap-4 mb-4">
            <span className="font-semibold text-gray-700">
              Additional Information
            </span>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.additionalInfoHusband.value)}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-white border border-dashed border-gray-300 rounded-md p-4">
                {renderValue(medicalInformation.additionalInfoWife.value)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


const TabMentalHealthFamily = ({ id, refreshTabDetails, setActiveTab,printPreviewMode }) => {
  const [mode, setMode] = useState("add");
  const [editingSection, setEditingSection] = useState(null);
  const [medicalInformationErrors, setMedicalInformationErrors] = useState({});
  const [medicalInformation, setMedicalInformation] = useState({


   presentComplaintsHusband: {
      label: "Husband Present Complaints",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
        historyOfPresentComplaintsHusband: {
      label: "Husband History of Present Complaints",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },

           passMedicalHistoryHusband: {
      label: "Husband Past Medical History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },





   presentComplaintsWife: {
      label: "Wife Husband Present Complaints",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
        historyOfPresentComplaintsWife: {
      label: "Wife Husband History of Present Complaints",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },

           passMedicalHistoryWife: {
      label: "Wife Husband Past Medical History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },

    historyOfSubstanceAbuseHusband: {
      label: "Husband Substance Abuse History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    historyOfSubstanceAbuseWife: {
      label: "Wife Substance Abuse History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },

    psychiatricFamilyHistoryHusband: {
      label: "Psychiatric Family History Hus",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    psychiatricFamilyHistoryWife: {
      label: "Psychiatric Family History Wife",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },

    improvedConditionsWife: {
      label: "Wife Improved Conditions",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    isHistoryOfPsychiatricTreatmentsHusband: {
      label: "Husband Psychiatric Treatment History",
      value: false,
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "boolean",
    },
    isHistoryOfPsychiatricTreatmentsWife: {
      label: "Wife Psychiatric Treatment History",
      value: false,
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "boolean",
    },
    isPsychiatricHospitalizationHusband: {
      label: "Husband Psychiatric Hospitalization",
      value: false,
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "boolean",
    },
    isPsychiatricHospitalizationWife: {
      label: "Wife Psychiatric Hospitalization",
      value: false,
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "boolean",
    },
    historyOfMentalIllnessHusband: {
      label: "Husband Mental Illness History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    historyOfMentalIllnessWife: {
      label: "Wife Mental Illness History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },

    historyOfACESHusband: {
      label: "Husband ACES History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "array",
    },
    historyOfACESWife: {
      label: "Wife ACES History",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "array",
    },
    additionalInfoHusband: {
      label: "Husband Additional Information",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
    additionalInfoWife: {
      label: "Wife Additional Information",
      value: "",
      isTouched: false,
      isValid: true,
      required: false,
      dataType: "string",
    },
  });

  const [initialMedicalInformation, setInitialMedicalInformation] = useState(null);
  const [modal, setModal] = useState({
    isOpen: false,
    message: "",
    type: "error",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [acesOptions, setAcesOptions] = useState([]);

  const loadDrpAces = async () => {
    const aces = await drpACES();
    setAcesOptions(aces.data.results[0]);
  };

  useEffect(() => {
    loadDropdowns();
  }, []);

  const loadDropdowns = async () => {
    await loadDrpAces();
  };

  const loadMedicalInformationData = async () => {
    setIsLoading(true);
    const result = await getPatientMedicalInfo(id);
    const patientData = result.data;
    if (patientData.error) {
      setModal({
        isOpen: true,
        message: patientData.error.message,
        type: "error",
      });
    }

    console.log('patientData',patientData)

    if (patientData) {
      setMedicalInformation({

        presentComplaintsHusband: {
          ...medicalInformation.presentComplaintsHusband,
          value: patientData.presentComplaintsHusband,
          isTouched: false,
          isValid: true,
        },
   historyOfPresentComplaintsHusband: {
          ...medicalInformation.historyOfPresentComplaintsHusband,
          value: patientData.historyOfPresentComplaintsHusband,
          isTouched: false,
          isValid: true,
        },

           passMedicalHistoryHusband: {
          ...medicalInformation.passMedicalHistoryHusband,
          value: patientData.passMedicalHistoryHusband,
          isTouched: false,
          isValid: true,
        },

        presentComplaintsWife: {
          ...medicalInformation.presentComplaintsWife,
          value: patientData.presentComplaintsWife,
          isTouched: false,
          isValid: true,
        },
   historyOfPresentComplaintsWife: {
          ...medicalInformation.historyOfPresentComplaintsWife,
          value: patientData.historyOfPresentComplaintsWife,
          isTouched: false,
          isValid: true,
        },

           passMedicalHistoryWife: {
          ...medicalInformation.passMedicalHistoryWife,
          value: patientData.passMedicalHistoryWife,
          isTouched: false,
          isValid: true,
        },







        historyOfSubstanceAbuseHusband: {
          ...medicalInformation.historyOfSubstanceAbuseHusband,
          value: patientData.historyOfSubstanceAbuseHusband || "",
        },
        historyOfSubstanceAbuseWife: {
          ...medicalInformation.historyOfSubstanceAbuseWife,
          value: patientData.historyOfSubstanceAbuseWife || "",
        },
        psychiatricFamilyHistoryHusband: {
          ...medicalInformation.psychiatricFamilyHistoryHusband,
          value: patientData.psychiatricFamilyHistoryHusband || "",
        },
        psychiatricFamilyHistoryWife: {
          ...medicalInformation.psychiatricFamilyHistoryWife,
          value: patientData.psychiatricFamilyHistoryWife || "",
        },
   
        isHistoryOfPsychiatricTreatmentsHusband: {
          ...medicalInformation.isHistoryOfPsychiatricTreatmentsHusband,
          value: patientData.isHistoryOfPsychiatricTreatmentsHusband || false,
        },
        isHistoryOfPsychiatricTreatmentsWife: {
          ...medicalInformation.isHistoryOfPsychiatricTreatmentsWife,
          value: patientData.isHistoryOfPsychiatricTreatmentsWife || false,
        },
        isPsychiatricHospitalizationHusband: {
          ...medicalInformation.isPsychiatricHospitalizationHusband,
          value: patientData.isPsychiatricHospitalizationHusband || false,
        },
        isPsychiatricHospitalizationWife: {
          ...medicalInformation.isPsychiatricHospitalizationWife,
          value: patientData.isPsychiatricHospitalizationWife || false,
        },
        historyOfMentalIllnessHusband: {
          ...medicalInformation.historyOfMentalIllnessHusband,
          value: patientData.historyOfMentalIllnessHusband || "",
        },
        historyOfMentalIllnessWife: {
          ...medicalInformation.historyOfMentalIllnessWife,
          value: patientData.historyOfMentalIllnessWife || "",
        },
  
        historyOfACESHusband: {
          ...medicalInformation.historyOfACESHusband,
          value:patientData.historyOfACESHusband ? patientData.historyOfACESHusband.split(";;"):'',
        },
        historyOfACESWife: {
          ...medicalInformation.historyOfACESWife,
          value:patientData.historyOfACESWife ? patientData.historyOfACESWife.split(";;"):'',
        },
        additionalInfoHusband: {
          ...medicalInformation.additionalInfoHusband,
          value: patientData.additionalInfoHusband || "",
        },
        additionalInfoWife: {
          ...medicalInformation.additionalInfoWife,
          value: patientData.additionalInfoWife || "",
        },
      });

      setIsLoading(false);
      setMode("edit");
    } else {
      setMode("add");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadMedicalInformationData();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      setMode("edit");
    }
  }, [id]);

  const validateField = (name, value, required, dataType) => {
    if (dataType === "string") {
      if (required && value.trim() === "") {
        return `${name} is required`;
      }
    }

    if (dataType === "array") {
      if (required && value.length === 0) {
        return `${name} is required`;
      }
    }

    if (dataType === "boolean") {
      if (required && value === null) {
        return `${name} is required`;
      }
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const { required, dataType } = medicalInformation[name];
    const finalValue = type === "checkbox" ? checked : value;
    const error = validateField(
      medicalInformation[name].label,
      finalValue,
      required,
      dataType
    );

    setMedicalInformation((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: finalValue,
        isTouched: true,
        isValid: error === "",
      },
    }));

    setMedicalInformationErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateMedicalInformation = () => {
    const errors = {};
    let isFormValid = true;
    const updatedInfo = { ...medicalInformation };

    Object.entries(medicalInformation).forEach(([key, field]) => {
      if (!field || typeof field !== "object" || !("value" in field)) return;

      const { value, dataType, required } = field;
      const errorMessage = validateField(
        field.label,
        value,
        required,
        dataType
      );

      if (errorMessage) {
        isFormValid = false;
        errors[key] = errorMessage;
        updatedInfo[key].isValid = false;
      } else {
        updatedInfo[key].isValid = true;
      }

      updatedInfo[key].isTouched = updatedInfo[key].isTouched || false;
    });

    setMedicalInformation(updatedInfo);
    setMedicalInformationErrors(errors);
    return isFormValid;
  };

  const handleSubmitMedicalInformation = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const isValid = validateMedicalInformation();
    if (isValid) {
      const payload = {
        patientId: id,


presentComplaintsHusband: medicalInformation.presentComplaintsHusband.value,
historyOfPresentComplaintsHusband: medicalInformation.historyOfPresentComplaintsHusband.value,
passMedicalHistoryHusband: medicalInformation.passMedicalHistoryHusband.value,

presentComplaintsWife: medicalInformation.presentComplaintsWife.value,
historyOfPresentComplaintsWife: medicalInformation.historyOfPresentComplaintsWife.value,
passMedicalHistoryWife: medicalInformation.passMedicalHistoryWife.value,









   historyOfSubstanceAbuseHusband: medicalInformation.historyOfSubstanceAbuseHusband.value,
        historyOfSubstanceAbuseWife: medicalInformation.historyOfSubstanceAbuseWife.value,
        psychiatricFamilyHistoryHusband: medicalInformation.psychiatricFamilyHistoryHusband.value,
        psychiatricFamilyHistoryWife: medicalInformation.psychiatricFamilyHistoryWife.value,
 
        isHistoryOfPsychiatricTreatmentsHusband: medicalInformation.isHistoryOfPsychiatricTreatmentsHusband.value,
        isHistoryOfPsychiatricTreatmentsWife: medicalInformation.isHistoryOfPsychiatricTreatmentsWife.value,
        isPsychiatricHospitalizationHusband: medicalInformation.isPsychiatricHospitalizationHusband.value,
        isPsychiatricHospitalizationWife: medicalInformation.isPsychiatricHospitalizationWife.value,
        historyOfMentalIllnessHusband: medicalInformation.historyOfMentalIllnessHusband.value,
        historyOfMentalIllnessWife: medicalInformation.historyOfMentalIllnessWife.value,
     
        historyOfACESHusband:medicalInformation.historyOfACESHusband.value ? medicalInformation.historyOfACESHusband.value.map((item) => ({
        name: item,
      })):null,
        historyOfACESWife:medicalInformation.historyOfACESWife.value ? medicalInformation.historyOfACESWife.value.map((item) => ({
        name: item,
      })):null,
        additionalInfoHusband: medicalInformation.additionalInfoHusband.value,
        additionalInfoWife: medicalInformation.additionalInfoWife.value,
        utcOffset: new Date().getTimezoneOffset(),
        pageName: "medical",
        isConfirm: true,
      };

      try {
        const res = await addMedicalInformationFamily(payload);
          
        if (res.data.error) {
          setModal({
            isOpen: true,
            message: res.data.error.message,
            type: "warning",
          });
          setIsSaving(false);
          return;
        }
      
        if (res.data.outputValues.responseStatus === "failed") {
          setModal({
            isOpen: true,
            message: res.data.outputValues.outputMessage,
            type: "warning",
          });
          setIsSaving(false);
          return;
        }

        setMode("edit");
        setIsSaving(false);
           loadMedicalInformationData();
        setModal({
          isOpen: true,
          message: res.data.outputValues.outputMessage,
          type: "success",
        });
        refreshTabDetails(Math.random());
      } catch (err) {
        setModal({
          isOpen: true,
          message: err.message,
          type: "error",
        });
        setIsSaving(false);
      }
    }
    setIsSaving(false);
  };

  const handleSubmitp = async (section) => {
    setIsSaving(true);
    const isValid = validateMedicalInformation();
    if (!isValid) {
      setIsSaving(false);
      return false;
    }

    const payload = {


presentComplaintsHusband:medicalInformation.presentComplaintsHusband.value,
historyOfPresentComplaintsHusband:medicalInformation.historyOfPresentComplaintsHusband.value,
passMedicalHistoryHusband:medicalInformation.passMedicalHistoryHusband.value,


presentComplaintsWife:medicalInformation.presentComplaintsWife.value,
historyOfPresentComplaintsWife:medicalInformation.historyOfPresentComplaintsWife.value,
passMedicalHistoryWife:medicalInformation.passMedicalHistoryWife.value,


      psychiatricFamilyHistoryHusband: medicalInformation.psychiatricFamilyHistoryHusband.value,
      psychiatricFamilyHistoryWife: medicalInformation.psychiatricFamilyHistoryWife.value,

      isHistoryOfPsychiatricTreatmentsHusband: !!medicalInformation.isHistoryOfPsychiatricTreatmentsHusband.value,
      isHistoryOfPsychiatricTreatmentsWife: !!medicalInformation.isHistoryOfPsychiatricTreatmentsWife.value,
      isPsychiatricHospitalizationHusband: !!medicalInformation.isPsychiatricHospitalizationHusband.value,
      isPsychiatricHospitalizationWife: !!medicalInformation.isPsychiatricHospitalizationWife.value,
      historyOfMentalIllnessHusband: medicalInformation.historyOfMentalIllnessHusband.value,
      historyOfMentalIllnessWife: medicalInformation.historyOfMentalIllnessWife.value,

      historyOfSubstanceAbuseHusband: medicalInformation.historyOfSubstanceAbuseHusband.value,
      historyOfSubstanceAbuseWife: medicalInformation.historyOfSubstanceAbuseWife.value,

     historyOfACESHusband:medicalInformation.historyOfACESHusband.value ? medicalInformation.historyOfACESHusband.value.map((item) => ({
        name: item,
      })):null,
        historyOfACESWife:medicalInformation.historyOfACESWife.value ? medicalInformation.historyOfACESWife.value.map((item) => ({
        name: item,
      })):null,
      additionalInfoHusband: medicalInformation.additionalInfoHusband.value,
      additionalInfoWife: medicalInformation.additionalInfoWife.value,
      utcOffset: new Date().getTimezoneOffset(),
      pageName: "medical",
      isConfirm: true,
    };

    console.log('handleSubmitp', payload);

    try {
      const res = await updateMedicalInformationFamily(id, payload);
        
      if (res.data.error) {
        setModal({
          isOpen: true,
          message: res.data.error.message,
          type: "warning",
        });
        setIsSaving(false);
        return;
      }
     
      if (res.data.outputValues.responseStatus === "failed") {
        setModal({
          isOpen: true,
          message: res.data.outputValues.outputMessage,
          type: "warning",
        });
        setIsSaving(false);
        return;
      }

      setIsSaving(false);
           loadMedicalInformationData();

      setModal({
        isOpen: true,
        message: res.data.outputValues.outputMessage,
        type: "success",
      });
      return true;
    } catch (err) {
      setModal({
        isOpen: true,
        message: err.message,
        type: "error",
      });
      setIsSaving(false);
      return false;
    }
  };

  const toggleSectionEdit = async (section) => {
    if (editingSection === section) {
      const isValid = await handleSubmitp(section);
      if (isValid) setEditingSection(null);
    } else {
      setEditingSection(section);
    }
  };

  const renderListItems = (value) => {
    if (!value) return <p className="text-gray-500 italic">N/A</p>;

    if (Array.isArray(value)) {
      return (
        <ul className="list-disc pl-5 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="text-gray-800">
              {item}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === "string" && value.includes(";")) {
      const items = value.split(";").filter((item) => item.trim());
      return (
        <ul className="list-disc pl-5 space-y-1">
          {items.map((item, index) => (
            <li key={index} className="text-gray-800">
              {item}
            </li>
          ))}
        </ul>
      );
    }

    return <p className="text-gray-800">{value}</p>;
  };

    const handleDescriptionChange = (fieldName, value) => {
    const { required, dataType } = medicalInformation[fieldName];
    const error = validateField(
      medicalInformation[fieldName].label,
      value,
      required,
      dataType
    );

    setMedicalInformation((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        value: value.split(";;").filter((item) => item.trim()),
        isTouched: true,
        isValid: error === "",
      },
    }));

    setMedicalInformationErrors((prev) => ({
      ...prev,
      [fieldName]: error,
    }));
  };

 

  useEffect(() => {
    setInitialMedicalInformation({ ...medicalInformation });
  }, [editingSection]);

  const handleCancel = (editingSection) => {
    if (initialMedicalInformation) {
      setMedicalInformation(initialMedicalInformation);
      setMedicalInformationErrors({});
    }
    setEditingSection(null);
  };

  return (
    <>
      <MessageModel
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false, message: "", type: "error" })}
        message={modal.message}
        type={modal.type}
      />

      {!isLoading ? (


!printPreviewMode ?
        <div className="px-8">
          <section className="mb-12">
            <div className="flex justify-between items-center mb-2 pb-2">
              <h3 className="text-xl font-semibold text-gray-800">
                Health Details
              </h3>
              {mode !== "add" && (
                <div className="flex space-x-4">
                  <EditButton
                    onClick={() => toggleSectionEdit("health")}
                    ariaLabel={
                      editingSection === "health"
                        ? "Save Health Details"
                        : "Edit Health Details"
                    }
                    disabled={isSaving}
                  >
                    {editingSection === "health"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </EditButton>
                  {editingSection === "health" && (
                    <button
                      onClick={() => handleCancel("health")}
                      className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all duration-200"
                      aria-label="Cancel Editing"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            {editingSection === "health" || mode === "add" ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div></div>
                  <h4 className="text-lg font-semibold text-sky-700">Husband</h4>
                  <h4 className="text-lg font-semibold text-sky-700">Wife</h4>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    Present Complaints
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="presentComplaintsHusband"
                      value={medicalInformation.presentComplaintsHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List Present Complaints"
                      aria-label="Husband Present Complaints"
                    />
                    {medicalInformationErrors.presentComplaintsHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.presentComplaintsHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="presentComplaintsWife"
                      value={medicalInformation.presentComplaintsWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List Present Complaints"
                      aria-label="Wife present Complaints"
                    />
                    {medicalInformationErrors.presentComplaintsWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.presentComplaintsWife}
                      </p>
                    )}
                  </div>
                </div>




         <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                   History of Present Complaints
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfPresentComplaintsHusband"
                      value={medicalInformation.historyOfPresentComplaintsHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List History of Present Complaints"
                      aria-label="Husband History of Present Complaints"
                    />
                    {medicalInformationErrors.historyOfPresentComplaintsHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfPresentComplaintsHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfPresentComplaintsWife"
                      value={medicalInformation.historyOfPresentComplaintsWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List History of Present Complaints"
                      aria-label="Wife history of present Complaints"
                    />
                    {medicalInformationErrors.historyOfPresentComplaintsWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfPresentComplaintsWife}
                      </p>
                    )}
                  </div>
                </div>





 <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                   Past Medical History
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="passMedicalHistoryHusband"
                      value={medicalInformation.passMedicalHistoryHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List Past Medical History"
                      aria-label="Husband passMedicalHistory"
                    />
                    {medicalInformationErrors.passMedicalHistoryHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.passMedicalHistoryHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="passMedicalHistoryWife"
                      value={medicalInformation.passMedicalHistoryWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List Past Medical History"
                      aria-label="Wife passMedicalHistory"
                    />
                    {medicalInformationErrors.passMedicalHistoryWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.passMedicalHistoryWife}
                      </p>
                    )}
                  </div>
                </div>






                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    History of Substance Abuse
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfSubstanceAbuseHusband"
                      value={medicalInformation.historyOfSubstanceAbuseHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List current issues"
                      aria-label="Husband History of Substance Abuse"
                    />
                    {medicalInformationErrors.historyOfSubstanceAbuseHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfSubstanceAbuseHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfSubstanceAbuseWife"
                      value={medicalInformation.historyOfSubstanceAbuseWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="List current issues"
                      aria-label="Wife History of Substance Abuse"
                    />
                    {medicalInformationErrors.historyOfSubstanceAbuseWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfSubstanceAbuseWife}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    Psychiatric Family History
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="psychiatricFamilyHistoryHusband"
                      value={medicalInformation.psychiatricFamilyHistoryHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Describe conditions"
                      aria-label="Husband Psychiatric Family History"
                    />
                    {medicalInformationErrors.psychiatricFamilyHistoryHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.psychiatricFamilyHistoryHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="psychiatricFamilyHistoryWife"
                      value={medicalInformation.psychiatricFamilyHistoryWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Describe conditions"
                      aria-label="Wife Psychiatric Family History"
                    />
                    {medicalInformationErrors.psychiatricFamilyHistoryWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.psychiatricFamilyHistoryWife}
                      </p>
                    )}
                  </div>
                </div>

              
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mb-5 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                  <div></div>
                  <h4 className="text-lg font-semibold text-sky-700 mb-4">Husband</h4>
                  <h4 className="text-lg font-semibold text-sky-700 mb-4">Wife</h4>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <strong className="text-sm">Present Complaints:</strong>
                  <div className="col-span-2">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.presentComplaintsHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.presentComplaintsWife.value}
                      </div>
                    </div>
                  </div>
                </div>




    <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <strong className="text-sm">History of Present Complaints:</strong>
                  <div className="col-span-2">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.historyOfPresentComplaintsHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.historyOfPresentComplaintsWife.value}
                      </div>
                    </div>
                  </div>
                </div>




                    <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <strong className="text-sm">Past Medical History:</strong>
                  <div className="col-span-2">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.passMedicalHistoryHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.passMedicalHistoryWife.value}
                      </div>
                    </div>
                  </div>
                </div>






                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <strong className="text-sm">History of Substance Abuse:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.historyOfSubstanceAbuseHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.historyOfSubstanceAbuseWife.value}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                  <strong className="text-sm">Psychiatric Family History:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.psychiatricFamilyHistoryHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                        {medicalInformation.psychiatricFamilyHistoryWife.value}
                      </div>
                    </div>
                  </div>
                </div>

       
              </div>
            )}
          </section>

          <section className="mb-12">
            <div className="flex justify-between items-center mb-2 pb-2">
              <h3 className="text-xl font-semibold text-gray-800">Treatment History</h3>
              {mode !== "add" && (
                <div className="flex space-x-4">
                  <EditButton
                    onClick={() => toggleSectionEdit("treatment")}
                    ariaLabel={
                      editingSection === "treatment"
                        ? "Save Treatment History"
                        : "Edit Treatment History"
                    }
                    disabled={isSaving}
                  >
                    {editingSection === "treatment"
                      ? isSaving
                        ? "Saving..."
                        : "Save"
                      : "Edit"}
                  </EditButton>
                  {editingSection === "treatment" && (
                    <button
                      onClick={() => handleCancel("treatment")}
                      className="flex items-center bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all duration-200"
                      aria-label="Cancel Editing"
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
            {editingSection === "treatment" || mode === "add" ? (
              <div className="space-y-4">
                {/* <div className="mb-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div></div>
                  <h4 className="text-lg font-semibold text-sky-700">Husband</h4>
                  <h4 className="text-lg font-semibold text-sky-700">Wife</h4>
                </div> */}

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6">
                  <label className="block text-sm font-medium text-gray-600 mt-2">
                    History of Psychiatric Treatments
                  </label>
                  {/* {JSON.stringify(medicalInformation.isHistoryOfPsychiatricTreatmentsHusband.value)} */}
                  <div className="col-span-2">
                    <div className="flex space-x-4 mt-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isHistoryOfPsychiatricTreatmentsHusband"
                          checked={medicalInformation.isHistoryOfPsychiatricTreatmentsHusband.value}
                          onChange={handleChange}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label="Husband psychiatric treatment history"
                        />
                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                      </label>
                    </div>
                    {medicalInformationErrors.isHistoryOfPsychiatricTreatmentsHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.isHistoryOfPsychiatricTreatmentsHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <div className="flex space-x-4 mt-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isHistoryOfPsychiatricTreatmentsWife"
                          checked={medicalInformation.isHistoryOfPsychiatricTreatmentsWife.value}
                          onChange={handleChange}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label="Wife psychiatric treatment history"
                        />
                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                      </label>
                    </div>
                    {medicalInformationErrors.isHistoryOfPsychiatricTreatmentsWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.isHistoryOfPsychiatricTreatmentsWife}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6">
                  <label className="block text-sm font-medium text-gray-600 mt-2">
                    Psychiatric Hospitalization
                  </label>
                  <div className="col-span-2">
                    <div className="flex space-x-4 mt-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isPsychiatricHospitalizationHusband"
                          checked={medicalInformation.isPsychiatricHospitalizationHusband.value}
                          onChange={handleChange}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label="Husband psychiatric hospitalization"
                        />
                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                      </label>
                    </div>
                    {medicalInformationErrors.isPsychiatricHospitalizationHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.isPsychiatricHospitalizationHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <div className="flex space-x-4 mt-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isPsychiatricHospitalizationWife"
                          checked={medicalInformation.isPsychiatricHospitalizationWife.value}
                          onChange={handleChange}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300"
                          aria-label="Wife psychiatric hospitalization"
                        />
                        <span className="ml-2 text-sm text-gray-700">Yes</span>
                      </label>
                    </div>
                    {medicalInformationErrors.isPsychiatricHospitalizationWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.isPsychiatricHospitalizationWife}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    History of Mental Illness
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfMentalIllnessHusband"
                      value={medicalInformation.historyOfMentalIllnessHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Provide mental illness history"
                      aria-label="Husband mental illness history"
                    />
                    {medicalInformationErrors.historyOfMentalIllnessHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfMentalIllnessHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfMentalIllnessWife"
                      value={medicalInformation.historyOfMentalIllnessWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Provide mental illness history"
                      aria-label="Wife mental illness history"
                    />
                    {medicalInformationErrors.historyOfMentalIllnessWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfMentalIllnessWife}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    History of Substance Abuse
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfSubstanceAbuseHusband"
                      value={medicalInformation.historyOfSubstanceAbuseHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Provide substance abuse history"
                      aria-label="Husband substance abuse history"
                    />
                    {medicalInformationErrors.historyOfSubstanceAbuseHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfSubstanceAbuseHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="historyOfSubstanceAbuseWife"
                      value={medicalInformation.historyOfSubstanceAbuseWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Provide substance abuse history"
                      aria-label="Wife substance abuse history"
                    />
                    {medicalInformationErrors.historyOfSubstanceAbuseWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfSubstanceAbuseWife}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6">
                  
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    History of ACES
                  </label>
                  
                  <div className="col-span-2">
                              {/* {JSON.stringify(medicalInformation.historyOfACESHusband.value)} */}
                    <DescriptionInput
                      patient={medicalInformation}
                      setValue={(value) => {
                        handleDescriptionChange("historyOfACESHusband", value);
                      }}
                      setPatient={(newPatient) => {
                        setMedicalInformation(newPatient);
                      }}
                      isEditing={editingSection === "treatment" || mode === "add"}
                      fieldName="historyOfACESHusband"
                      label="Husband ACES History"
                      placeholder="Select or enter ACES history"
                      descriptionOptions={acesOptions}
                      isTypeable={false}
                    />
                    {medicalInformationErrors.historyOfACESHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfACESHusband}
                      </p>
                    )}
                  </div>
        
                  <div className="col-span-2">
                    {/* {JSON.stringify(medicalInformation.historyOfACESWife.value)} */}
                    <DescriptionInput
                      patient={medicalInformation}
                      setValue={(value) => {
                        handleDescriptionChange("historyOfACESWife", value);
                      }}
                      setPatient={(newPatient) => {
                        setMedicalInformation(newPatient);
                      }}
                      isEditing={editingSection === "treatment" || mode === "add"}
                      fieldName="historyOfACESWife"
                      label="Wife ACES History"
                      placeholder="Select or enter ACES history"
                      descriptionOptions={acesOptions}
                      isTypeable={false}
                    />
                    {medicalInformationErrors.historyOfACESWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.historyOfACESWife}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6">
                  <label className="block text-sm font-medium text-gray-700 mt-2">
                    Additional Information
                  </label>
                  <div className="col-span-2">
                    <VoiceToText
                      name="additionalInfoHusband"
                      value={medicalInformation.additionalInfoHusband.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Provide additional details"
                      aria-label="Husband additional info"
                    />
                    {medicalInformationErrors.additionalInfoHusband && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.additionalInfoHusband}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <VoiceToText
                      name="additionalInfoWife"
                      value={medicalInformation.additionalInfoWife.value}
                      onChange={handleChange}
                      className="mt-1 w-full p-3 border text-sm border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all duration-200"
                      rows="4"
                      placeholder="Provide additional details"
                      aria-label="Wife additional info"
                    />
                    {medicalInformationErrors.additionalInfoWife && (
                      <p className="mt-1 text-sm text-red-600">
                        {medicalInformationErrors.additionalInfoWife}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
                <div className="space-y-4">
                {/* <div className=" grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div></div>
                  <h4 className="text-lg font-semibold text-sky-700 mb-4">Husband</h4>
                  <h4 className="text-lg font-semibold text-sky-700 mb-4">Wife</h4>
                </div> */}
         
               
                   <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                   <strong className="text-sm">Psychiatric Treatment History:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                                {medicalInformation.isHistoryOfPsychiatricTreatmentsHusband.value ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                      {medicalInformation.isHistoryOfPsychiatricTreatmentsWife.value ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>
               



 <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
              <strong className="text-sm">Psychiatric Hospitalization:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                                {medicalInformation.isPsychiatricHospitalizationHusband.value ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                      {medicalInformation.isPsychiatricHospitalizationWife.value ? "Yes" : "No"}
                      </div>
                    </div>
                  </div>
                </div>


            
                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
       <strong className="text-sm whitespace-pre-line">Mental Illness History:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                                    {medicalInformation.historyOfMentalIllnessHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                       {medicalInformation.historyOfMentalIllnessWife.value}
                      </div>
                    </div>
                  </div>
                </div>

       


         
                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
              <strong className="text-sm whitespace-pre-line">Substance Abuse History:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                                    {medicalInformation.historyOfSubstanceAbuseHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                       {medicalInformation.historyOfSubstanceAbuseWife.value}
                      </div>
                    </div>
                  </div>
                </div>

      <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                <strong className="text-sm whitespace-pre-line">ACES History:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                      {renderListItems(medicalInformation.historyOfACESHusband.value)}
                    </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="whitespace-pre-line">
                      {renderListItems(medicalInformation.historyOfACESWife.value)}
                    </div>
                    </div>
                  </div>
                </div>

           
               

                <div className="mb-5 grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
                   <strong className="text-sm whitespace-pre-line">Additional Information:</strong>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                                    {medicalInformation.additionalInfoHusband.value}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="whitespace-pre-line">
                       {medicalInformation.additionalInfoWife.value}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </section>

          {mode === "add" && (
            <div className="flex justify-end space-x-4 mt-6">
              <button
                type="button"
                onClick={handleSubmitMedicalInformation}
                className="flex items-center bg-sky-600 text-white px-6 py-3 rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
                aria-label="Save and go to next tab"
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>
:
<PrintMentalHealthFamilyA4 medicalInformation={medicalInformation} />


      ) : (
        <LoadingSpinner />
      )}
    </>
  );
};

export default TabMentalHealthFamily;